package dev.lingvo.ops.components

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import dev.lingvo.site.ds.FontStack
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.StylePropertyValue

private val OpsReducedMotion = CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("reduce"))

val OpsShellRootStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().backgroundColor(colorMode.toSitePalette().surfaceBase)
            .color(colorMode.toSitePalette().textPrimary).styleModifier {
                property("min-height", "100dvh")
                property("overflow-wrap", "anywhere")
                property("--ops-motion", "${Motion.StandardMs}ms")
                property("scrollbar-color", "${colorMode.toSitePalette().borderStrong} ${colorMode.toSitePalette().surfaceBase}")
                property("caret-color", colorMode.toSitePalette().accent.toString())
            }
    }
    cssRule(" ::selection") {
        Modifier.backgroundColor(colorMode.toSitePalette().accentSubtle).color(colorMode.toSitePalette().textPrimary)
    }
    cssRule(" a") { Modifier.styleModifier { property("text-underline-offset", "3px") } }
    cssRule(" main h1") {
        Modifier.fontWeight(750).styleModifier {
            property("font-family", FontStack.Display.joinToString { "\"$it\"" })
            property("font-size", "${TypeScale.Heading}")
            property("line-height", "1.15")
            property("letter-spacing", "-0.025em")
            property("margin", "0")
            property("text-wrap", "balance")
        }
    }
    cssRule(" main th, main td") { Modifier.fontSize(TypeScale.Small) }
    cssRule(" main [role=\"region\"][tabindex=\"0\"]") {
        Modifier.styleModifier {
            property("min-width", "0")
            property("max-width", "100%")
            property("overflow-x", "auto")
            property("overscroll-behavior-inline", "contain")
        }
    }
    cssRule(" main table") {
        Modifier.styleModifier {
            property("font-variant-numeric", "tabular-nums")
            property("min-width", "0")
        }
    }
    cssRule(" main table:has(thead tr > :nth-child(4))") {
        Modifier.styleModifier { property("min-width", "48rem") }
    }
    // Identity/name columns keep a reading measure; wider tables scroll inside their own region.
    // A bounded child still wraps hostile identifiers, instead of growing the document itself.
    cssRule(" main th:first-child, main td:first-child") {
        Modifier.styleModifier {
            property("min-width", "12rem")
            property("max-width", "24rem")
        }
    }
    cssRule(" main td:first-child > a") {
        Modifier.styleModifier {
            property("display", "block")
            property("min-width", "12rem")
            property("max-width", "24rem")
            property("overflow-wrap", "anywhere")
        }
    }
    cssRule(" main th") { Modifier.styleModifier { property("white-space", "nowrap") } }
    cssRule(" main > div") { Modifier.styleModifier { property("min-width", "0") } }
    cssRule(OpsReducedMotion) { Modifier.styleModifier { property("--ops-motion", "0ms") } }
}

val OpsChromeStyle = CssStyle.base {
    Modifier.fillMaxWidth().backgroundColor(colorMode.toSitePalette().surface).styleModifier {
        property("position", "sticky")
        property("top", "0")
        property("z-index", "30")
        property("border-bottom", "1px solid ${colorMode.toSitePalette().border}")
    }
}

val OpsHeaderStyle = CssStyle {
    base {
        Modifier.padding(topBottom = Space.MD, leftRight = Space.MD).styleModifier {
            property("display", "grid")
            property("grid-template-columns", "minmax(0, 1fr) auto")
            property("gap", "12px 16px")
            property("align-items", "center")
            property("width", "100%")
            property("max-width", "1424px")
            property("margin", "0 auto")
        }
    }
    Breakpoint.MD {
        Modifier.padding(leftRight = Space.LG).styleModifier {
            property("grid-template-columns", "auto minmax(200px, 1fr) auto")
            property("gap", "24px")
        }
    }
}

val OpsHeaderLockupStyle = CssStyle {
    base {
        Modifier.color(colorMode.toSitePalette().textPrimary).styleModifier {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "12px")
            property("text-decoration", "none")
            property("font-family", FontStack.Display.joinToString { "\"$it\"" })
            property("font-size", "21px")
            property("font-weight", "750")
            property("white-space", "nowrap")
            property("border-radius", "8px")
        }
    }
    focusVisible { Modifier.styleModifier { property("outline", "2px solid ${colorMode.toSitePalette().accent}"); property("outline-offset", "4px") } }
}

val OpsSearchSlotStyle = CssStyle {
    base { Modifier.styleModifier { property("grid-column", "1 / -1"); property("min-width", "0") } }
    Breakpoint.MD { Modifier.styleModifier { property("grid-column", "auto"); property("max-width", "520px") } }
}

val OpsIdentityStyle = CssStyle {
    base {
        Modifier.color(colorMode.toSitePalette().textSecondary).fontSize(TypeScale.Small).styleModifier {
            property("grid-column", "1 / -1")
            property("display", "flex")
            property("gap", "8px")
            property("align-items", "center")
            property("justify-content", "space-between")
            property("min-width", "0")
        }
    }
    Breakpoint.MD {
        Modifier.styleModifier { property("grid-column", "auto"); property("justify-content", "flex-end"); property("gap", "12px") }
    }
}

val OpsDesktopNavStyle = CssStyle {
    base { Modifier.styleModifier { property("display", "none") } }
    Breakpoint.MD {
        Modifier.styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("gap", "4px")
            property("max-width", "1424px")
            property("margin", "0 auto")
            property("padding", "0 24px 12px")
        }
    }
}

val OpsMobileToggleStyle = CssStyle {
    base { Modifier.styleModifier { property("display", "inline-flex") } }
    Breakpoint.MD { Modifier.styleModifier { property("display", "none") } }
    cssRule(" [aria-expanded=\"true\"] svg") { Modifier.styleModifier { property("transform", "rotate(180deg)") } }
    cssRule(" svg") { Modifier.styleModifier { property("transition", "transform var(--ops-motion) ${Motion.EaseOut}") } }
}

/** Only explicit navigation disclosure moves; route content and data never enter or count up. */
val OpsMobileNavStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("display", "grid")
            property("grid-template-rows", "0fr")
            property("transition", "grid-template-rows var(--ops-motion) ${Motion.EaseOut}")
        }
    }
    cssRule("[data-open=\"true\"]") { Modifier.styleModifier { property("grid-template-rows", "1fr") } }
    cssRule(" > div") { Modifier.styleModifier { property("min-height", "0"); property("overflow", "hidden") } }
    cssRule(" nav") { Modifier.padding(top = Space.XXS, bottom = Space.MD, leftRight = Space.MD).styleModifier {
        property("display", "grid"); property("grid-template-columns", "repeat(2, minmax(0, 1fr))"); property("gap", "4px")
    } }
    Breakpoint.MD { Modifier.styleModifier { property("display", "none") } }
}

val OpsNavItemStyle = CssStyle {
    base {
        Modifier.color(colorMode.toSitePalette().textSecondary).fontSize(TypeScale.BodySmall).fontWeight(600)
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("min-height", "40px")
                property("padding", "8px 12px")
                property("border-radius", "8px")
                property("text-decoration", "none")
                property("white-space", "nowrap")
                property("transition", "background-color ${Motion.FastMs}ms ${Motion.EaseOut}, color ${Motion.FastMs}ms ${Motion.EaseOut}")
            }
    }
    cssRule(":hover") { Modifier.backgroundColor(colorMode.toSitePalette().surfaceSunken).color(colorMode.toSitePalette().textPrimary) }
    cssRule("[aria-current=\"page\"], [aria-current=\"page\"]:hover") {
        Modifier.backgroundColor(colorMode.toSitePalette().accentSubtle).color(colorMode.toSitePalette().accent).fontWeight(700)
    }
    focusVisible { Modifier.styleModifier { property("outline", "2px solid ${colorMode.toSitePalette().accent}"); property("outline-offset", "2px") } }
    cssRule(OpsReducedMotion) { Modifier.styleModifier { property("transition", "none") } }
}

/** Kept public for the standalone login and callback; the console owns its own measure. */
val OpsContentStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().styleModifier {
            property("max-width", "1424px")
            property("margin", "0 auto")
            property("padding", "24px 16px 48px")
            property("min-width", "0")
            property("scroll-margin-top", "180px")
        }
    }
    Breakpoint.MD { Modifier.styleModifier { property("padding", "24px 24px 48px") } }
}

val OpsSkipLinkStyle = CssStyle {
    base {
        Modifier.backgroundColor(colorMode.toSitePalette().accent).color(colorMode.toSitePalette().accentContrast)
            .styleModifier {
                property("position", "fixed")
                property("top", "8px")
                property("left", "16px")
                property("z-index", "100")
                property("padding", "12px 16px")
                property("border-radius", "8px")
                property("transform", "translateY(-160%)")
            }
    }
    focusVisible { Modifier.styleModifier { property("transform", "none"); property("outline", "2px solid ${colorMode.toSitePalette().textPrimary}"); property("outline-offset", "2px") } }
}
